/**
 * Unified Opportunity Lifecycle Model
 * 
 * One record per deal — evolves from Lead through Appointment, Follow-Up, and Close.
 * Replaces the former separate Lead + Quote systems.
 */

import type { ProjectType, ProjectConfiguration } from "./types";

/* ═══════ LIFECYCLE STAGES ═══════ */

export type OpportunityStage =
  | "Lead"
  | "Quote Created"
  | "Appointment Scheduled"
  | "Appointment Completed"
  | "Follow-Up Needed"
  | "Closed Won"
  | "Closed Lost";

export const OPPORTUNITY_STAGES: OpportunityStage[] = [
  "Lead",
  "Quote Created",
  "Appointment Scheduled",
  "Appointment Completed",
  "Follow-Up Needed",
  "Closed Won",
  "Closed Lost",
];

export const STAGE_COLORS: Record<OpportunityStage, string> = {
  "Lead": "bg-blue-100 text-blue-800 border-blue-200",
  "Quote Created": "bg-cyan-100 text-cyan-800 border-cyan-200",
  "Appointment Scheduled": "bg-amber-100 text-amber-800 border-amber-200",
  "Appointment Completed": "bg-purple-100 text-purple-800 border-purple-200",
  "Follow-Up Needed": "bg-orange-100 text-orange-800 border-orange-200",
  "Closed Won": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Closed Lost": "bg-gray-100 text-gray-500 border-gray-200",
};

/* ═══════ STAGE HELPERS ═══════ */

export function isWonStage(stage: OpportunityStage): boolean {
  return stage === "Closed Won";
}

export function isClosedStage(stage: OpportunityStage): boolean {
  return stage === "Closed Won" || stage === "Closed Lost";
}

export function isActiveStage(stage: OpportunityStage): boolean {
  return !isClosedStage(stage);
}

export function isPreAppointment(stage: OpportunityStage): boolean {
  return stage === "Lead" || stage === "Quote Created" || stage === "Appointment Scheduled";
}

export function isPostAppointment(stage: OpportunityStage): boolean {
  return stage === "Appointment Completed" || stage === "Follow-Up Needed" || isClosedStage(stage);
}

/* ═══════ WON OUTCOME CLASSIFICATION ═══════ */

export type WonOutcomeType = "net" | "canceled" | "finance_declined" | "pending";

export const WON_OUTCOME_LABELS: Record<WonOutcomeType, string> = {
  net: "Net",
  canceled: "Canceled",
  finance_declined: "Finance Declined",
  pending: "Pending — Rescission",
};

export const WON_OUTCOME_COLORS: Record<WonOutcomeType, string> = {
  net: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  canceled: "bg-destructive/10 text-destructive",
  finance_declined: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
};

/** Check if an opportunity resulted in net (realized) revenue */
export function isNetWon(opp: Opportunity): boolean {
  return opp.stage === "Closed Won" && opp.wonOutcome === "net";
}

/** Check if an opportunity is in the rescission period */
export function isPendingRescission(opp: Opportunity): boolean {
  return opp.stage === "Closed Won" && (!opp.wonOutcome || opp.wonOutcome === "pending");
}

/* ═══════ DISPLAY REFERENCE ═══════ */

let _oppCounter = 0;
export function generateOpportunityId(): string {
  _oppCounter++;
  return `OPP-${String(_oppCounter).padStart(4, "0")}`;
}

export function buildOpportunityReference(sequence: number): string {
  return `OPP-${String(sequence).padStart(4, "0")}`;
}

export function formatDisplayReference(id: string): string {
  if (!id) return "OPP-0000";

  const trimmed = id.trim();
  const oppMatch = trimmed.match(/^OPP-(\d+)$/i);
  if (oppMatch) return `OPP-${oppMatch[1].padStart(4, "0")}`;

  const qteMatch = trimmed.match(/^QTE-(\d+)$/i);
  if (qteMatch) return `OPP-${qteMatch[1].padStart(4, "0")}`;

  return trimmed;
}

export function formatLeadReference(id: string | number): string {
  if (typeof id === "number") {
    return `LEAD-${String(id).padStart(3, "0")}`;
  }
  // UUID primary keys: use last 4 hex chars as a short stable suffix
  const trimmed = id.trim();
  const uuidSuffix = trimmed.replace(/-/g, "").slice(-4).toUpperCase();
  if (uuidSuffix.length === 4) return `LEAD-${uuidSuffix}`;
  // Numeric string fallback
  const num = parseInt(trimmed.replace(/\D/g, ""), 10);
  if (!num || isNaN(num) || num > 9_999_999) return "LEAD-????";
  return `LEAD-${String(num).padStart(3, "0")}`;
}

/* ═══════ STAGE CHANGE TRACKING ═══════ */

export interface StageChange {
  id: string;
  fromStage: OpportunityStage | null;
  toStage: OpportunityStage;
  changedAt: string;
  changedBy: string;
}

/* ═══════ ACTIVITY ═══════ */

export interface OpportunityActivity {
  id: string;
  type: "call" | "note" | "follow_up" | "email" | "system";
  description: string;
  createdAt: string;
  createdBy: string;
}

/* ═══════ OWNERSHIP ═══════ */

export interface OwnershipChange {
  id: string;
  fromRep: string | null;
  toRep: string;
  changedAt: string;
  changedBy: string;
}

/* ═══════ DISPOSITION ═══════ */

export type DispositionReason =
  | "no_show"
  | "not_home"
  | "cancelled"
  | "refused_entry"
  | "safety_concern"
  | "wrong_address"
  | "not_qualified"
  | "other";

export type FollowUpType = "rep" | "manager" | "none";

export interface NoDemoDisposition {
  reason: DispositionReason;
  homeownerPresent: boolean;
  decisionMakerPresent: boolean;
  mainReasonNoDemoText: string;
  pricingDiscussed: boolean;
  stillLiveOpportunity: boolean;
  followUpType: FollowUpType;
  followUpNotes: string;
  bestContactMethod: string;
  followUpDateTime?: string;
  noFollowUpReason?: string;
  completedAt: string;
  completedBy: string;
}

/* ═══════ FOLLOW-UP TASKS ═══════ */

export type FollowUpSource = "manual" | "system";

export interface FollowUpTask {
  id: string;
  opportunityId: string;
  type: FollowUpType;
  assignedTo: string;
  dueDate: string;
  status: "pending" | "completed" | "overdue";
  notes: string;
  createdAt: string;
  disposition: NoDemoDisposition;
  customerName: string;
  customerPhone: string;
  // ── Enhanced follow-up fields ──
  dispositionReason?: string;
  executionMethod?: "call" | "text" | "call_and_text";
  priority?: "high" | "medium" | "low";
  suggestedScript?: string;
  sequence?: FollowUpTouch[];
  completedAt?: string;
  outcome?: "re_engaged" | "no_response" | "closed_won" | "closed_lost";
  thankYouSent?: boolean;
  thankYouSentAt?: string;
  // ── Automation-aware fields ──
  source?: FollowUpSource;
  isAutomated?: boolean;

  // ── Canonical Hand-off Routing (Phase 1) ──
  // Routing fields used by the centralized handoff resolver. Optional so that
  // legacy follow-up tasks (assignedTo string only) remain backward-compatible.
  // Resolution priority: assignedToUserId → assignedToRoleKey → legacy alias map.
  /** Stable role key (e.g. "head_of_installation"). Routes the task to the role seat. */
  assignedToRoleKey?: string;
  /** Optional direct user assignment. Targets a specific person. */
  assignedToUserId?: string;
  /** Display label preserved for UI. NEVER used for routing. */
  assignedToLabel?: string;

  // ── Source operational object linkage ──
  // Optional pointers to the entity that triggered the hand-off so the inbox
  // can render context and link back. At most one is typically populated.
  jobId?: string;
  issueId?: string;
  estimateId?: string;
  actionId?: string;

  // ── Maturity-Ready Stubs (Tier 2/3 — UI not built yet) ──
  /** SLA budget in hours from createdAt. Surfaced once escalation ships. */
  slaHours?: number;
  /** Set when the assignee acknowledges receipt. Two-step flow ships post-Phase-1. */
  acknowledgedAt?: string;

  // ── Phase 2: Actionable Hand-off Framework (all optional, backward-compatible) ──
  /**
   * Canonical machine-readable intent for what kind of work this hand-off
   * represents. When absent, `handoffActions.inferIntent` derives it from
   * source linkage + assignee role. Explicit declared intent always wins.
   */
  handoffIntent?: HandoffIntent;
  /** Optional canonical action identifier the hand-off resolves to. */
  actionKey?: string;
  /** Human-readable label override for the resolved action's primary CTA. */
  actionLabel?: string;
  /**
   * Optional structured metadata used by the action resolver to launch the
   * right modal/route. Storage is permissive (`unknown`); the resolver
   * narrows it via a discriminated union internally.
   */
  actionPayload?: unknown;
  /** Structured outcome captured when the hand-off is completed. */
  completionOutcome?: HandoffCompletionOutcome;

  // ── Phase 3: Workflow Lineage Primitives (additive, generic, forward-compatible) ──
  /**
   * The hand-off this one was chained from. Set by the "Complete & chain"
   * flow in RoleHandoffInbox. Lets `getWorkflowLineage` walk the chain
   * deterministically without parsing free-text reason fragments.
   */
  originHandoffId?: string;
  /**
   * Generic origin classification. Kept intentionally broad so future
   * systems (persisted Action Queue, AI suggestion engine) can populate
   * it without schema churn:
   *   "manual"      — operator created via Push-Mode prompt
   *   "system"      — created by a deterministic engine (e.g. installer wrap)
   *   "ai_suggested" — derived from AI signal (when Action Queue persists)
   *   "chained"     — produced by Complete-and-chain
   */
  workflowSourceType?: "manual" | "system" | "ai_suggested" | "chained";
  /** Free-form id of the originating record (action item, signal, etc.). */
  workflowSourceId?: string;

  // ── Phase 5: Hand-off ↔ Action Queue Convergence (additive) ──
  /**
   * The persisted Action Queue item that represents the downstream work for
   * this hand-off. Set when chaining produces a queue item, OR when a queue
   * item is later linked back to its originating hand-off. Enables the
   * convergence sync rules in `workflowConvergence` to resolve this hand-off
   * deterministically when the linked queue item is completed.
   *
   * IMPORTANT: linkage is additive only — pre-Phase-5 chained pairs remain
   * unlinked and behave exactly as before.
   */
  linkedQueueItemId?: string;
}

/* ═══════ HAND-OFF INTENT TAXONOMY (Phase 2) ═══════ */

/**
 * Canonical intent types. Optional on FollowUpTask; defaults via inference
 * to "general_handoff". Add new intents here, then map them in
 * src/data/handoffActions.ts — the only place that interprets them.
 */
export type HandoffIntent =
  | "install_review"
  | "material_confirmation"
  | "schedule_coordination"
  | "finance_followup"
  | "service_resolution"
  | "estimate_revision"
  | "permit_followup"
  | "customer_contact"
  | "general_handoff";

/**
 * Structured completion outcomes. Schema-only for now — UI exposes
 * `completed` (default Complete button) and `completed_and_created_followup`
 * (the "Complete & hand off again" affordance). Other variants are reserved
 * for Tier 2 escalation/audit work.
 */
export type HandoffCompletionOutcome =
  | "completed"
  | "completed_and_advanced"
  | "completed_and_created_followup"
  | "completed_but_blocked"
  | "reassigned"
  | "deferred";

export interface FollowUpTouch {
  dayOffset: number;
  method: "call" | "text" | "call_and_text";
  label: string;
  script: string;
  status: "pending" | "completed" | "skipped";
  completedAt?: string;
  outcome?: "re_engaged" | "no_response" | "closed_won" | "closed_lost" | "pending";
}

export interface ConsultationProgress {
  currentStep: number;
  startedAt: string;
  lastUpdated: string;
  arrivalData?: {
    withHomeowner: boolean | null;
    name: string;
    phone: string;
    address: string;
    secondaryName: string;
    secondaryPhone: string;
    primaryEmail: string;
    secondaryEmail: string;
  };
  discoveryData?: {
    category: string;
    painPoints: string[];
    notes: string;
  };
  configData?: ProjectConfiguration;
  completedScopes?: Array<{
    id: string;
    category: string;
    painPoints: string[];
    notes: string;
    config: ProjectConfiguration;
    noAddOns: boolean;
  }>;
  permitRequired?: boolean;
  closeStage?: string;
  selectedPayment?: "cash" | "financing";
}


/* ═══════ CONTRACT & PRICING SUB-TYPES ═══════ */

// These remain here for import convenience; detailed types stay in quoteTypes.ts

import type {
  ContractDocument,
  CustomerVerification,
  PricingGuardrails,
  PricingOverride,
  PricingStatus,
  QuoteScenario,
  DepositInfo,
} from "./quoteTypes";

/* ═══════ UNIFIED OPPORTUNITY ═══════ */

export interface Opportunity {
  quote_number: any;
  quoteId: unknown;
  leadId: unknown;
  id: string;
  displayReference: string;

  // ── Customer ──
  primaryName: string;
  secondaryName: string;
  primaryPhone: string;
  secondaryPhone: string;
  primaryEmail: string;
  secondaryEmail: string;

  // ── Address ──
  address: string;
  city: string;
  state: string;
  zip: string;

  // ── Project ──
  projectType: ProjectType;
  leadSource: string;
  leadCost?: number;

  // ── Lifecycle ──
  stage: OpportunityStage;
  stageHistory: StageChange[];

  // ── Ownership ──
  assignedRep: string;
  ownershipHistory: OwnershipChange[];

  // ── Activity ──
  activities: OpportunityActivity[];

  // ── Appointment ──
  appointmentTime?: string;

  // ── Disposition ──
  disposition?: NoDemoDisposition;

  // ── Follow-Up ──
  followUpType?: FollowUpType;
  followUpDueDate?: string;
  dispositionReason?: string;
  dispositionNotes?: string;

  // ── Build / Configuration ──
  projectConfig?: ProjectConfiguration;
  totalEstimate: number;
  scopeOfWork?: string;

  // ── Pricing ──
  pricingGuardrails?: PricingGuardrails;
  pricingOverride?: PricingOverride;
  pricingStatus?: PricingStatus;

  // ── Contract ──
  contract?: ContractDocument;
  verification?: CustomerVerification;
  deposit?: DepositInfo;
  scenarios?: QuoteScenario[];

  // ── Links ──
  linkedJobId?: string;

  // Consultation Progress
  consultationProgress?: ConsultationProgress;

  // ── Timestamps ──
  createdAt: string;
  updatedAt: string;
  notes: string;

  // ── Won/Lost Tracking ──
  wonAt?: string;
  wonOutcome?: WonOutcomeType;
  lostAt?: string;
  lostReason?: string;
  lockedAt?: string;
  lockedBy?: string;
  presentedAt?: string;

  // ── Backward Compat (populated from canonical fields) ──
  /** @deprecated Use primaryName */ customerName?: string;
  /** @deprecated Use primaryPhone */ customerPhone?: string;
  /** @deprecated Use primaryEmail */ customerEmail?: string;
  /** @deprecated Use getFullAddress() */ customerAddress?: string;
  /** Customer FK for database-backed flows. Optional for migration safety. */
  customerId?: string;
  /** Organization FK for database-backed flows. Optional for migration safety. */
  organizationId?: string;
  /** @deprecated Use assignedRep */ createdBy?: string;
  /** @deprecated Use stage */ status?: OpportunityStage;
  /** @deprecated Use getOpportunityLabel() */ quoteName?: string;
  /** @deprecated Use linkedJobId */ convertedJobId?: string;
  /** @deprecated */ appointmentId?: string;
  /** @deprecated */ linkedQuoteId?: string;
   // ── Multi-Tenant Ownership ──
   /** Tenant/company this opportunity belongs to. Optional for migration safety. */
   tenantId?: string;

   // ── Approval/Rejection (Integrity Engine) ──
   decisionStatus?: string | null;
   validationStatus?: string | null;
   rejectionReason?: string | null;
   rejectionNotes?: string | null;
   acceptedAt?: string | null;
   rejectedAt?: string | null;
   isDuplicate?: boolean | null;
   matchedDuplicateBy?: string | null;
 }

/* ═══════ CONVENIENCE GETTERS ═══════ */

/** Full formatted address string */
export function getFullAddress(opp: Opportunity): string {
  return [opp.address, opp.city, `${opp.state} ${opp.zip}`].filter(Boolean).join(", ");
}

/** Derive a display name like "Rodriguez Shower Remodel" */
export function getOpportunityLabel(opp: Opportunity): string {
  const lastName = opp.primaryName.split(" ").pop() || opp.primaryName;
  return `${lastName} ${opp.projectType}`;
}
